import { useEffect, useRef } from "react";

/**
 * useWakeLock
 * Mantiene la pantalla del dispositivo encendida mientras la app esté abierta
 * y visible en primer plano. Se libera automáticamente cuando:
 *   - el componente se desmonta,
 *   - la pestaña pasa a segundo plano (visibilitychange => hidden),
 *   - el navegador suelta el lock por otra razón.
 * Se re-adquiere automáticamente cuando la pestaña vuelve a estar visible.
 *
 * Usa la Web Wake Lock API (screen). Si el navegador no la soporta, no hace nada.
 */
export default function useWakeLock(enabled = true) {
  const lockRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined") return;
    if (!("wakeLock" in navigator)) return;

    let cancelled = false;

    const acquire = async () => {
      if (document.visibilityState !== "visible") return;
      if (lockRef.current) return;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await lock.release(); } catch { /* noop */ }
          return;
        }
        lockRef.current = lock;
        lock.addEventListener("release", () => {
          if (lockRef.current === lock) lockRef.current = null;
        });
      } catch {
        // Algunos navegadores rechazan si el documento no está totalmente activo o
        // si el usuario revocó permiso. Silencioso a propósito.
      }
    };

    const release = async () => {
      const lock = lockRef.current;
      lockRef.current = null;
      if (lock) {
        try { await lock.release(); } catch { /* noop */ }
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        acquire();
      } else {
        release();
      }
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
  }, [enabled]);
}
